import { gql, useQuery } from '@apollo/client';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import * as React from 'react';

import Button from '../../components/Button';
import CopyButton from '../../components/CopyButton';

import {
  describeAlertSettings,
  describeTransactionCoverage,
  formatMajorUnits,
  holdingsState,
  isGapNotable,
} from './accountDetailView';
import InvestmentTransactionsTable from './InvestmentTransactionsTable';
import PositionsTable from './PositionsTable';
import RawJsonBlock from './RawJsonBlock';

import type {
  AdminGetFinancialAccountDetailQuery,
  AdminGetFinancialAccountDetailQueryVariables,
} from './__generated__/AccountDetailPanel.gql';

/** Operation name, for the scene's `refetchQueries` after a sync. */
export const ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL_OPERATION = 'AdminGetFinancialAccountDetail';

// `AdminFinancialAccountDetail` must stay unnormalized (no `keyFields`): this
// document and the family-wide one select different depths of `investmentAudit`,
// and a shared cache entry would let one overwrite the other's positions.
const ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL = gql`
  query AdminGetFinancialAccountDetail($familyId: ID!, $financialAccountId: ID!) {
    adminGetFinancialAccountDetails(familyId: $familyId, financialAccountId: $financialAccountId) {
      financialAccountId
      externalId
      canonicalName
      name
      currencyCode
      currentBalance
      lowBalanceThreshold
      creditUtilizationThreshold
      disableThreshold
      createdAt
      updatedAt
      version
      rawJson
      balanceRawJson
      investmentAudit {
        relinkCandidate
        investmentsConsented
        currentBalance
        holdingsValue
        cashEquivalentValue
        balanceGap
        latestSnapshotAt
        positionCount
        positions {
          securityId
          externalSecurityId
          tickerSymbol
          securityName
          securityType
          quantity
          institutionPrice
          institutionPriceAsOf
          institutionValue
          costBasis
          isoCurrencyCode
          unofficialCurrencyCode
          isCashEquivalent
          missingPrice
          missingCostBasis
          unidentifiedSecurity
          quantityChangedWithoutPriceChange
          holdingRawJson
          securityRawJson
        }
      }
      investmentTransactionCoverage {
        count
        earliestDate
        latestDate
      }
      investmentTransactions {
        investmentTransactionId
        externalId
        dataSource
        securityId
        tickerSymbol
        securityName
        date
        transactionDatetime
        name
        type
        subtype
        quantity
        price
        amount
        fees
        isoCurrencyCode
        unofficialCurrencyCode
        rawJson
      }
    }
  }
`;

const OPEN_TOGGLE_CLASS = 'border-fg-brand-primary text-fg-brand-primary';
const DT_CLASS = 'text-fg-tertiary uppercase tracking-wider';
const DD_CLASS = 'text-fg-secondary flex min-w-0 items-center gap-1 font-mono';

interface IAccountDetailPanelProps {
  familyId: string;
  financialAccountId: string;
}

/**
 * The expanded detail of one account: identity, alert settings, the holdings
 * audit with its positions and the stored transactions on an investment
 * account, and the stored provider payloads. Mounted only while its row is open, so the heavy single-account
 * read happens on expand and refreshes on every re-open.
 */
const AccountDetailPanel = ({ familyId, financialAccountId }: IAccountDetailPanelProps) => {
  const [showAccountJson, setShowAccountJson] = React.useState(false);
  const [showBalanceJson, setShowBalanceJson] = React.useState(false);

  const { data, loading, error, refetch } = useQuery<
    AdminGetFinancialAccountDetailQuery,
    AdminGetFinancialAccountDetailQueryVariables
  >(ADMIN_GET_FINANCIAL_ACCOUNT_DETAIL, {
    variables: { familyId, financialAccountId },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const detail = data?.adminGetFinancialAccountDetails[0];

  if (!detail) {
    if (loading) {
      return <div className="text-fg-secondary text-xs">Loading account detail…</div>;
    }
    if (error) {
      return (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-fg-error-primary">Could not load this account's detail.</span>
          <Button onClick={() => void refetch()} color="secondary" size="xs">
            Retry
          </Button>
        </div>
      );
    }
    return <div className="text-fg-tertiary text-xs italic">This account is no longer visible to the family.</div>;
  }

  const audit = detail.investmentAudit;
  const currency = { isoCurrencyCode: detail.currencyCode };
  const state = audit ? holdingsState(audit) : null;
  const coverage = detail.investmentTransactionCoverage;
  const transactions = detail.investmentTransactions ?? [];

  return (
    <div className="flex min-w-0 flex-col gap-3 text-xs">
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)_max-content_minmax(0,1fr)] gap-x-4 gap-y-1">
        <dt className={DT_CLASS}>Account id</dt>
        <dd className={DD_CLASS}>
          <span className="select-all truncate">{detail.financialAccountId}</span>
          <CopyButton value={detail.financialAccountId} label="Copy account id" successMessage="Account id copied." />
        </dd>
        <dt className={DT_CLASS}>Created</dt>
        <dd className={DD_CLASS} title={new Date(detail.createdAt).toLocaleString()}>
          {formatTimeAgo(detail.createdAt)}
        </dd>

        <dt className={DT_CLASS}>Plaid account id</dt>
        <dd className={DD_CLASS}>
          <span className="select-all truncate">{detail.externalId}</span>
          <CopyButton
            value={detail.externalId}
            label="Copy Plaid account id"
            successMessage="Plaid account id copied."
          />
        </dd>
        <dt className={DT_CLASS}>Updated</dt>
        <dd className={DD_CLASS} title={new Date(detail.updatedAt).toLocaleString()}>
          {formatTimeAgo(detail.updatedAt)} · v{detail.version}
        </dd>

        <dt className={DT_CLASS}>Provider name</dt>
        <dd className={DD_CLASS}>
          <span className="truncate">{detail.canonicalName}</span>
          {detail.name && detail.name !== detail.canonicalName && (
            <span className="text-fg-tertiary truncate">· renamed “{detail.name}”</span>
          )}
        </dd>
        <dt className={DT_CLASS}>Alert settings</dt>
        <dd className={DD_CLASS}>{describeAlertSettings(detail)}</dd>
      </dl>

      {audit && (
        <div className="flex min-w-0 flex-col gap-2">
          <div className="grid grid-cols-6 gap-3">
            <Stat label="Balance" value={formatMajorUnits(audit.currentBalance, currency)} />
            <Stat label="Holdings value" value={formatMajorUnits(audit.holdingsValue, currency)} />
            <Stat label="Cash-equivalent" value={formatMajorUnits(audit.cashEquivalentValue, currency)} />
            <Stat
              label="Gap"
              value={formatMajorUnits(audit.balanceGap, currency)}
              isWarning={isGapNotable(audit.balanceGap, audit.positions)}
            />
            <Stat label="Snapshot" value={audit.latestSnapshotAt ? formatTimeAgo(audit.latestSnapshotAt) : 'never'} />
            <Stat
              label="Investments consent"
              value={audit.investmentsConsented ? 'granted' : audit.relinkCandidate ? 'not granted' : 'unread'}
              isWarning={audit.relinkCandidate}
            />
          </div>

          {state === 'relink' && (
            <div className="text-fg-warning-primary">
              Investments were not consented on this connection — holdings need a relink.
            </div>
          )}
          {state === 'never' && (
            <div className="text-fg-tertiary italic">
              No holdings snapshot yet — use Sync holdings on the connection.
            </div>
          )}
          {state === 'empty' && audit.latestSnapshotAt && (
            <div className="text-fg-tertiary italic">
              The latest snapshot, {formatTimeAgo(audit.latestSnapshotAt)}, has no positions.
            </div>
          )}
          {state === 'populated' && (
            <PositionsTable
              positions={audit.positions}
              holdingsValue={audit.holdingsValue ?? null}
              currencyCode={detail.currencyCode ?? null}
            />
          )}
        </div>
      )}

      {coverage && (
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <span className={DT_CLASS}>Investment transactions</span>
            <span className={coverage.count === 0 ? 'text-fg-tertiary italic' : 'text-fg-secondary font-mono'}>
              {describeTransactionCoverage(coverage, transactions.length)}
            </span>
          </div>
          {transactions.length > 0 && <InvestmentTransactionsTable transactions={transactions} />}
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => setShowAccountJson((open) => !open)}
            aria-expanded={showAccountJson}
            color="secondary"
            size="xs"
            className={showAccountJson ? OPEN_TOGGLE_CLASS : undefined}
          >
            Account JSON
          </Button>
          <Button
            onClick={() => setShowBalanceJson((open) => !open)}
            aria-expanded={showBalanceJson}
            color="secondary"
            size="xs"
            className={showBalanceJson ? OPEN_TOGGLE_CLASS : undefined}
          >
            Balance JSON
          </Button>
        </div>
        {(showAccountJson || showBalanceJson) && (
          <div className="grid min-w-0 grid-cols-2 gap-3">
            {showAccountJson && <RawJsonBlock title="Account JSON" value={detail.rawJson} />}
            {showBalanceJson && <RawJsonBlock title="Balance JSON" value={detail.balanceRawJson} />}
          </div>
        )}
      </div>
    </div>
  );
};

interface IStatProps {
  label: string;
  value: string;
  isWarning?: boolean;
}

const Stat = ({ label, value, isWarning = false }: IStatProps) => (
  <div className="flex min-w-0 flex-col">
    <span className={DT_CLASS}>{label}</span>
    <span className={isWarning ? 'text-fg-warning-primary truncate' : 'text-fg-primary truncate'}>{value}</span>
  </div>
);

export default AccountDetailPanel;
